export type ReceiptPaperWidth = "58mm" | "80mm";

export interface ReceiptPrintLine {
  description: string;
  quantity: string;
  total: string;
}

export interface ReceiptPrintModel {
  direction: "ltr" | "rtl";
  language: string;
  paperWidth?: ReceiptPaperWidth | null;
  title: string;
  venueName: string;
  receiptNumber: string;
  issuedAt: string;
  customerName?: string | null;
  lines: ReceiptPrintLine[];
  subtotal: string;
  serviceFee: string;
  tax: string;
  total: string;
  paid: string;
  balance: string;
  footer?: string | null;
  labels: {
    customer: string;
    issued: string;
    line: string;
    qty: string;
    total: string;
    subtotal: string;
    service: string;
    vat: string;
    paid: string;
    balance: string;
  };
}

function escapeHtml(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  })[character] ?? character);
}

function safePaperWidth(value: ReceiptPaperWidth | null | undefined): ReceiptPaperWidth {
  return value === "58mm" ? "58mm" : "80mm";
}

export function buildReceiptPrintDocument(model: ReceiptPrintModel) {
  const text = (value: string | number | null | undefined) => escapeHtml(String(value ?? ""));
  const width = safePaperWidth(model.paperWidth);
  const lineItems = model.lines.map((line) => `
    <div class="line">
      <span class="description">${text(line.description)}</span>
      <span class="quantity">${text(line.quantity)}</span>
      <span class="amount">${text(line.total)}</span>
    </div>`).join("");

  return `<!doctype html>
<html lang="${text(model.language)}" dir="${text(model.direction)}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${text(model.title)} - ${text(model.receiptNumber)}</title>
  <style>
    @page { size: ${width} auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { width: ${width}; padding: 4mm; color: #111827; font: 10pt/1.35 Arial, Tahoma, sans-serif; }
    .receipt { width: 100%; }
    .center { text-align: center; }
    .brand { font-size: 15pt; font-weight: 800; letter-spacing: .08em; }
    .venue { margin-top: 2mm; font-weight: 700; overflow-wrap: anywhere; }
    .meta { margin-top: 1mm; color: #4b5563; font-size: 8pt; }
    .rule { border: 0; border-top: 1px dashed #6b7280; margin: 3mm 0; }
    .columns, .line, .total-row { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; column-gap: 2mm; align-items: baseline; }
    .columns { color: #4b5563; font-size: 8pt; font-weight: 700; }
    .line { padding: 1.2mm 0; border-bottom: 1px dotted #d1d5db; }
    .description { overflow-wrap: anywhere; }
    .quantity, .amount { text-align: end; white-space: nowrap; }
    .summary { margin-top: 2mm; display: grid; gap: 1mm; }
    .total-row { grid-template-columns: 1fr auto; font-weight: 700; }
    .grand-total { border-top: 1px solid #111827; padding-top: 2mm; font-size: 12pt; }
    .footer { margin-top: 4mm; color: #4b5563; font-size: 8pt; text-align: center; overflow-wrap: anywhere; }
  </style>
</head>
<body>
  <main class="receipt">
    <div class="center">
      <div class="brand">GAAMED</div>
      <div class="venue">${text(model.venueName)}</div>
      <div class="meta">${text(model.title)} · ${text(model.receiptNumber)}</div>
      <div class="meta">${text(model.labels.issued)}: ${text(model.issuedAt)}</div>
      ${model.customerName ? `<div class="meta">${text(model.labels.customer)}: ${text(model.customerName)}</div>` : ""}
    </div>
    <hr class="rule">
    <div class="columns"><span>${text(model.labels.line)}</span><span>${text(model.labels.qty)}</span><span>${text(model.labels.total)}</span></div>
    ${lineItems || `<div class="line"><span class="description">&mdash;</span><span></span><span></span></div>`}
    <div class="summary">
      <div class="total-row"><span>${text(model.labels.subtotal)}</span><span>${text(model.subtotal)}</span></div>
      <div class="total-row"><span>${text(model.labels.service)}</span><span>${text(model.serviceFee)}</span></div>
      <div class="total-row"><span>${text(model.labels.vat)}</span><span>${text(model.tax)}</span></div>
      <div class="total-row grand-total"><span>${text(model.labels.total)}</span><span>${text(model.total)}</span></div>
      <div class="total-row"><span>${text(model.labels.paid)}</span><span>${text(model.paid)}</span></div>
      <div class="total-row"><span>${text(model.labels.balance)}</span><span>${text(model.balance)}</span></div>
    </div>
    ${model.footer ? `<div class="footer">${text(model.footer)}</div>` : ""}
  </main>
</body>
</html>`;
}

async function waitForReceiptLayout(document: Document, popup: Window) {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
  const nextFrame = () => new Promise<void>((resolve) => {
    if (typeof popup.requestAnimationFrame === "function") {
      popup.requestAnimationFrame(() => popup.requestAnimationFrame(() => resolve()));
    } else {
      setTimeout(resolve, 0);
    }
  });
  await nextFrame();
}

/** Open synchronously from the click handler. Do not add async work here. */
export function openReceiptPrintWindow(host: Window = window) {
  try {
    const popup = host.open("", "_blank", "width=420,height=720");
    if (!popup) {
      return null;
    }

    popup.document.open();
    popup.document.write("<!doctype html><html><head><title>Receipt</title></head><body></body></html>");
    popup.document.close();
    return popup;
  } catch {
    return null;
  }
}

export function closeReceiptPrintWindow(popup: Window | null | undefined) {
  try {
    popup?.close();
  } catch {
    // A popup can be closed by the browser or the user before rendering fails.
  }
}

/** Populate and print a window that was opened synchronously by the caller. */
export async function printReceiptDocument(popup: Window, documentHtml: string) {
  try {
    popup.document.open();
    popup.document.write(documentHtml);
    popup.document.close();
    await waitForReceiptLayout(popup.document, popup);
  } catch {
    closeReceiptPrintWindow(popup);
    return false;
  }

  return await new Promise<boolean>((resolve) => {
    popup.addEventListener("afterprint", () => {
      closeReceiptPrintWindow(popup);
      resolve(true);
    }, { once: true });
    try {
      popup.focus();
      popup.print();
    } catch {
      closeReceiptPrintWindow(popup);
      resolve(false);
    }
  });
}
